import classNames from "classnames";
import React from "react";
import {
    agentAvatarThumbnailRef,
    memberAvatarThumbnailRef,
} from "../../FirebaseBackend";
import CachedAvatar from "./CachedAvatar";
import "./Avatar.css";
import { useMembersMap } from "../../common/hooks/useMembers";
import type { Agent } from "../../common/types/Agent";
import { takeAllAfter, takeFirst } from "../../common/utils/array.util";
import { AlbertineServiceAgentSenderId } from "../../common/validate";
import { nameInitials } from "../../utils/members.util";
import { agentFirstnames } from "../../utils/name.util";
import Asset, { Assets } from "./Asset";

type Props = {
    className?: string | undefined;
};

type MemberAvatarProps = {
    memberId?: string | null | undefined;
    member?: { id: string; firstName: string; lastName: string } | null | undefined;
    size: "big" | "mid" | "small";
    alt?: string;
} & Props;

const MemberAvatar = function MemberAvatar(props: MemberAvatarProps) {
    const { memberId: memberIdProp, member: memberProp, alt, className, size } = props;
    const memberMap = useMembersMap();

    const memberId = memberIdProp ?? memberProp?.id;
    if (!memberId) {
        return null;
    }

    const memberData = memberIdProp ? memberMap[memberIdProp] : memberProp;

    const sizeClassName = `avatar__${size}`;
    return (
        <CachedAvatar
            alt={alt}
            className={classNames(sizeClassName, className)}
            storageRef={memberAvatarThumbnailRef(memberId)}
            emptyString={
                memberData
                    ? nameInitials(memberData.firstName, memberData.lastName)
                    : undefined
            }
        />
    );
};

type InitialsProps = {
    firstName: string;
    lastName: string;
    size: "big" | "mid" | "small";
} & Props;

const Initials = function Initials(props: InitialsProps) {
    const { className, firstName, lastName, size } = props;
    const sizeClassName = `avatar__${size}`;

    return (
        <div className={classNames(sizeClassName, className)}>
            {nameInitials(firstName, lastName)}
        </div>
    );
};

type AgentAvatarProps = {
    agentId: string | null | undefined;
    size: "huge" | "big" | "mid" | "small";
    alt?: string;
} & Props;

const AgentAvatar = function AgentAvatar(props: AgentAvatarProps) {
    const { agentId, alt, className, size } = props;
    const sizeClassName = `avatar__${size}`;

    if (!agentId) return null;
    if (agentId === AlbertineServiceAgentSenderId) {
        return (
            <div
                className={classNames(
                    "agent-avatar__service-agent",
                    sizeClassName,
                    className,
                )}
            >
                <Asset src={Assets.albertineIcon} />
            </div>
        );
    }
    return (
        <CachedAvatar
            storageRef={agentAvatarThumbnailRef(agentId)}
            alt={alt}
            className={classNames(sizeClassName, className)}
        />
    );
};

type AgentAvatarWithSecondaryProps = {
    primaryAgentId: string | null | undefined;
    secondaryAgentId: string | null | undefined;
    alt?: string;
} & Props;

const AgentAvatarWithSecondary = function AgentAvatars(
    props: AgentAvatarWithSecondaryProps,
) {
    const { primaryAgentId, alt, secondaryAgentId, className } = props;
    return (
        <div className={classNames("agent-avatar-with-secondary", className)}>
            <AgentAvatar
                alt={alt}
                agentId={primaryAgentId}
                className={classNames("agent-avatar__primary", className)}
                size="mid"
            />

            {secondaryAgentId && (
                <AgentAvatar
                    alt={alt}
                    agentId={secondaryAgentId}
                    className="agent-avatar__secondary"
                    size="small"
                />
            )}
        </div>
    );
};

type AgentAvatarListProps = {
    agents: Agent[];
    numberOfAvatars?: number;
    className?: string;
    showNames?: boolean;
};

const AgentAvatarList = function AgentAvatarList(props: AgentAvatarListProps) {
    const { agents, numberOfAvatars, className, showNames } = props;
    const maxLength = numberOfAvatars || 3;
    const lengthOverMaximum =
        agents.length > maxLength ? agents.length - maxLength : undefined;

    return (
        <div className={classNames("agent-avatar-list", className)}>
            {takeFirst(maxLength, agents).map((agent) => (
                <AgentAvatar
                    key={agent.id}
                    agentId={agent.id}
                    size="small"
                    className="agent-avatar-list__avatar"
                />
            ))}
            {showNames && (
                <span className="agent-avatar-list__names">
                    {agentFirstnames(takeFirst(maxLength, agents))}
                    {lengthOverMaximum && (
                        <span>&nbsp;+{takeAllAfter(maxLength, agents).length}</span>
                    )}
                </span>
            )}
        </div>
    );
};

export {
    AgentAvatar,
    AgentAvatarList,
    AgentAvatarWithSecondary,
    Initials,
    MemberAvatar,
};
